import {inject, Injectable} from '@angular/core';
import moment from 'moment';
import {SettingsService} from './settings.service';
import {IsoDateWithPrecision} from './definitions/iso-date-with-precision';
import {MetaField} from './definitions/meta-field';
import {FieldDateInfoService} from "./field-date-info.service";

@Injectable({
  providedIn: 'root'
})
export class DateToolsService {
  // Injections
  private readonly fieldDateInfoService: FieldDateInfoService = inject(FieldDateInfoService);
  private readonly settings: SettingsService = inject(SettingsService);

  private static getUtcDate(dateIn: Date) {
    const date = new Date(dateIn.getTime());
    const offset = date.getTimezoneOffset();
    const osh = Math.floor(offset / 60);
    const osm = offset % 60;
    const hours = date.getHours();
    const minutes = date.getMinutes();
    date.setHours(hours + osh);
    date.setMinutes(minutes + osm);
    return date;
  }

  private static getDateSplitter(dateFormat: string) {
    const splitters = ['.', '-', '/', ':'];
    let res: string;
    for (const test of splitters) {
      // Check for position > 0 in order for negative years to work
      if (dateFormat.indexOf(test) > 0) {
        res = test;
        break;
      }
    }
    return res;
  }

  // splitData avoids empty split parts
  private static splitDate(date: string, splitter: string) {
    const res = [];
    const split = date.split(splitter);
    for (const element of split) {
      if (element && element !== '0') {
        res.push(element);
      } else {
        break;
      }
    }
    return res;
  }

  private static validateDateParts(dateParts: any) {
    const res = {
      valid: true,
      error: ''
    };

    if (dateParts.dayPart !== -1 && (dateParts.dayPart < 1 || dateParts.dayPart > 31)) {
      res.valid = false;
      res.error = 'TRANS__VALIDATION__INVALID_DAY_NUMBER';
    }

    if (dateParts.monthPart !== -1 && (dateParts.monthPart < 0 || dateParts.monthPart > 11)) {
      res.valid = false;
      res.error = 'TRANS__VALIDATION__INVALID_MONTH_NUMBER';
    }

    return res;
  }

  private static getDateFromDateParts(p: any) {
    const res = {date: undefined, valid: false, solrDate: null};
    try {
      res.date = new Date(p.yearPart, p.monthPart, p.dayPart, 0, 0, 0, 0);
      if (res.date.getTime()) {
        res.valid = true;
      } else {
        res.date = null;
      }
    } catch (e) {
      console.warn('Invalid date', e);
    }
    return res;
  }

  private static createDate(year: number, month: number, day: number, hour: number, minute: number, sec: number) {
    let res: Date;
    if (year >= 0) {
      res = new Date(DateToolsService.isoDateStr(year, month, day, hour, minute, sec));
    } else {
      res = new Date(Date.UTC(year, month - 1, day, hour, minute, sec, 0));
    }
    return res;
  }

  private static isoDateStr(year: number, month: number, day: number, hour: number, minute: number, sec: number) {
    return DateToolsService.numAsFixedStr(year, 4) + '-' +
      DateToolsService.numAsFixedStr(month, 2) + '-' +
      DateToolsService.numAsFixedStr(day, 2) + 'T' +
      DateToolsService.numAsFixedStr(hour, 2) + ':' +
      DateToolsService.numAsFixedStr(minute, 2) + ':' +
      DateToolsService.numAsFixedStr(sec, 2) + 'Z';
  }

  private static numAsFixedStr(num: number, digits: number) {
    const str = num.toString().split('-').pop();
    return '0000'.substring(0, digits - str.length) + str;
  }

  public isoDateStringToUtcDate(isoDate: string): Date {
    let res = null;
    if (isoDate) {
      if (isoDate.startsWith('-')) {
        // BC date is in format -yyyy-mm-ddThh:mm:ssZ from backend. Need to conveert to -00yyyy-mm-dd hh:mm:ss bc
        res = new Date('-00' + isoDate.substring(1, 9));
      } else {
        const year = Number(isoDate.substring(0, 4));
        const month = Number(isoDate.substring(5, 7)) - 1;
        const day = Number(isoDate.substring(8, 10));
        const hours = Number(isoDate.substring(11, 13));
        const minutes = Number(isoDate.substring(14, 16));
        const seconds = Number(isoDate.substring(17, 19));
        const utcTime = Date.UTC(year, month, day, hours, minutes, seconds);
        res = new Date(utcTime);
      }
    }
    return res;
  }

  public isoDateToString(isoDate: string, precision?: string): string {
    let textDate = '';
    if (isoDate) {
      const utcDate = this.isoDateStringToUtcDate(isoDate);
      if (precision === 'year') {
        textDate = this.getLocalStringDateYear(utcDate);
      } else if (precision === 'month') {
        textDate = this.getLocalStringDateYearMonth(utcDate);
      } else if (precision === 'datetime') {
        textDate = this.getLocalStringDateTime(utcDate);
      } else {
        textDate = this.getLocalStringDate(utcDate);
      }
    }
    return textDate;
  }

  public validateDate(dateString: string): IsoDateWithPrecision {
    let newDate: Date, newPrecision: string;
    const res = new IsoDateWithPrecision();
    if (!dateString) {
      return res;
    }
    const splitChar = DateToolsService.getDateSplitter(dateString);
    if (this.hasIllegalDateChars(dateString, splitChar)) {
      res.dd_error_message = 'TRANS__VALIDATION__ILLEGAL_CHARACTERS_IN_DATE';
      return res;
    }
    const dateParts = this.getDateParts(dateString, true);
    const validate = DateToolsService.validateDateParts(dateParts);
    if (!validate.valid) {
      res.dd_error_message = validate.error;
      return res;
    }

    // y, yy, yyy now is based on year 2000 not 1900
    const yearStr = (dateParts.yearPart).toString();
    const yearLength = yearStr.length;
    if (yearLength <= 2 && !yearStr.startsWith('-')) {
      dateParts.yearPart = '2000'.substring(0, 4 - yearLength) + dateParts.yearPart;
    }

    newPrecision = 'date';
    if (dateParts.dayPart !== -1 && dateParts.monthPart !== -1) {
      newDate = new Date(Date.UTC(dateParts.yearPart, dateParts.monthPart, dateParts.dayPart));
    } else if (dateParts.monthPart !== -1) {
      newPrecision = 'month';
      newDate = new Date(Date.UTC(dateParts.yearPart, dateParts.monthPart));
    } else {
      newPrecision = 'year';
      newDate = DateToolsService.createDate(dateParts.yearPart, 1, 1, 0, 0, 0);
    }

    // Ensure that `newDate` is a valid date
    if (isNaN(newDate.getTime())) {
      res.dd_error_message = 'TRANS__VALIDATION__ILLEGAL_DATE';
      return res;
    }

    return this.dateToPrecisionDate(newDate, newPrecision);
  }

  public dateToPrecisionDate(date: Date, precision?: string): IsoDateWithPrecision {
    precision = precision || 'date';
    const res = new IsoDateWithPrecision();
    res.isoDate = this.dateToIsoString(date);
    res.precision = precision;
    return res;
  }

  // Unfortunately, the ISO  format provided in JavaScript differs from the ISO format used in the application, so
  // need to strip millisecond information from the JavaScript generated format
  public dateToIsoString(date: Date) {
    let res = date.toISOString().substring(0, 19) + 'Z';
    if (date.getFullYear() < 0) {
      // Year < 0 is converted to -00yyyy in ISO format, need to convert to 'yyyy-mm-dd bc'
      res = '-' + res.substring(3);
    }
    return res;
  }

  public solrDateToLocalStringDate(solrDate: string) {
    let res = '';
    if (solrDate) {
      const datePart = solrDate.substring(0, 10);
      const date = new Date(datePart);
      res = this.getLocalStringDate(date);
    }
    return res;
  }

  public localStringDateToSolrDate(stringDate: string, propName?: string) {
    let dateRes = {
      solrDate: '',
      date: null,
      valid: true
    };
    if (stringDate) {
      dateRes = this.getDateFromLocalStringDate(stringDate);
      if (dateRes.date) {
        if (propName === 'end') {
          dateRes.solrDate = moment(dateRes.date).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        } else {
          dateRes.solrDate = moment(dateRes.date).format('YYYY-MM-DDTHH:mm:ss');
        }
        if (dateRes.solrDate) {
          dateRes.solrDate += 'Z';
        } else {
          dateRes.valid = false;
          console.warn('Invalid date filtering');
        }
      }
    }
    return dateRes;
  }

  public checkExpiredIsoDate(metaField: MetaField, isoDateString: string, statusType: string) {
    let res = false;
    const dateInfo = this.fieldDateInfoService.getFieldDateInfo(metaField);
    if (dateInfo?.check_expired) {
      const currentDate = this.getTodayUtcTime();
      if (isoDateString && currentDate > isoDateString) {
        if (statusType !== 'closed') {
          res = true;
        }
      }
    }
    return res;
  }

  public getTodayUtcTime(): string {
    const todayUtc = DateToolsService.getUtcDate(new Date());
    return this.dateToIsoString(todayUtc);
  }

  getDateFromLocalStringDate(localDate: string) {
    const p = this.getDateParts(localDate, false);
    return DateToolsService.getDateFromDateParts(p);
  }

  private hasIllegalDateChars(dateString: string, sep: string) {
    let hasIllegalChars = false;
    for (let index = 0; index < dateString.length; index++) {
      if (!this.isLegalDateChar(dateString.charAt(index), sep)) {
        hasIllegalChars = true;
      }
    }
    return hasIllegalChars;
  }

  private isLegalDateChar(char: string, sep: string) {
    if (this.isNumeric(char) || char === '-') {
      return true;
    }
    return char === sep;
  }

  public isNumeric(char: string) {
    return /^\d+$/.test(char);
  }

  private getLocalStringDateTime(date: Date) {
    return moment(date).format(this.settings.getClientConfig().LOCAL_DATE_FORMAT_FULL_WITH_TIME);
  }

  private getLocalStringDate(date: Date) {
    return moment(date).format(this.settings.getClientConfig().LOCAL_DATE_FORMAT_FULL);
  }

  private getLocalStringDateYearMonth(date: Date) {
    return moment(date).format(this.settings.getClientConfig().LOCAL_DATE_FORMAT_YEAR_MONTH);
  }

  private getLocalStringDateYear(date: Date) {
    let dateStr: string;
    const fullYear = date.getFullYear();
    if (fullYear >= 0) {
      dateStr = moment(date).format(this.settings.getClientConfig().LOCAL_DATE_FORMAT_YEAR);
    } else {
      dateStr = fullYear.toString();
    }
    return dateStr;
  }

  private getDatePartIndexes(dateFormat: string, dateSplitter: string) {
    let dayPartIndex = -1, monthPartIndex = -1, yearPartIndex = -1;
    dateFormat.split(dateSplitter).forEach(
      (dateFormatPart, index) => {
        if (dateFormatPart === 'DD') {
          dayPartIndex = index;
        } else if (dateFormatPart === 'MM') {
          monthPartIndex = index;
        } else if (dateFormatPart === 'YYYY') {
          yearPartIndex = index;
        }
      }
    );
    return {
      day: dayPartIndex,
      month: monthPartIndex,
      year: yearPartIndex
    };
  }

  getDateParts(localDate: string, setNullIfMissing: boolean) {
    const clientConfig = this.settings.getClientConfig();
    let dayPart = 1, monthPart = 0, yearPart: any;
    let dateFormat: string;
    const dateSplitter = DateToolsService.getDateSplitter(localDate);
    if (setNullIfMissing) {
      dayPart = -1;
      monthPart = -1;
    }

    if (!dateSplitter) {
      yearPart = localDate;
    } else {
      const localDateSplit = DateToolsService.splitDate(localDate, dateSplitter);
      if (localDateSplit.length === 1) {
        dateFormat = clientConfig.LOCAL_DATE_FORMAT_YEAR;
      } else if (localDateSplit.length === 2) {
        dateFormat = clientConfig.LOCAL_DATE_FORMAT_YEAR_MONTH;
      } else {
        dateFormat = clientConfig.LOCAL_DATE_FORMAT_FULL;
      }
      const dpi = this.getDatePartIndexes(dateFormat, dateSplitter);
      yearPart = Number(localDateSplit[dpi.year]);
      if (dpi.  month !== -1) {
        monthPart = Number(localDateSplit[dpi.month]) - 1;
      }
      if (dpi.day !== -1) {
        dayPart = Number(localDateSplit[dpi.day]);
      }
    }

    return {
      dayPart: dayPart,
      monthPart: monthPart,
      yearPart: yearPart
    };
  }
}
